/**
 * Booking Related Pages
 * Cat sitting booking and success pages
 */
import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, X } from "lucide-react";

import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Calendar as CalendarComponent } from "../components/ui/calendar";

import { useAuth } from "../contexts/AuthContext";
import { API } from "../lib/api";

const BookPage = () => {
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedDates, setSelectedDates] = useState({ start: null, end: null });
  const [catName, setCatName] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [bookedDates, setBookedDates] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/packages`).then(r => setPackages(r.data));
    const now = new Date();
    // Get booked dates from bookings
    axios.get(`${API}/availability?month=${now.getMonth() + 1}&year=${now.getFullYear()}`).then(r => setBookedDates(r.data.booked_dates || []));
    // Get blocked dates from calendar events
    axios.get(`${API}/calendar/events?month=${now.getMonth() + 1}&year=${now.getFullYear()}`).then(r => {
      const events = r.data.events || [];
      const blocked = [];
      events.forEach(evt => {
        const start = new Date(evt.start_date);
        const end = new Date(evt.end_date);
        let current = new Date(start);
        while (current <= end) {
          blocked.push(current.toISOString().split("T")[0]);
          current.setDate(current.getDate() + 1);
        }
      });
      setBlockedDates(blocked);
    }).catch(() => {});
  }, []);

  const handleBook = async () => {
    if (!user) { navigate("/login"); return; }
    if (!selectedPackage || !selectedDates.start || !catName) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      const bookingRes = await axios.post(`${API}/bookings`, {
        package_id: selectedPackage.package_id,
        cat_name: catName,
        start_date: selectedDates.start.toISOString().split("T")[0],
        end_date: (selectedDates.end || selectedDates.start).toISOString().split("T")[0],
        notes
      }, { withCredentials: true });

      // Create checkout
      const checkoutRes = await axios.post(`${API}/checkout/create`, {
        booking_id: bookingRes.data.booking_id,
        origin_url: window.location.origin
      }, { withCredentials: true });

      window.location.href = checkoutRes.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed");
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = () => {
    if (!selectedPackage || !selectedDates.start) return 0;
    if (selectedPackage.package_id === "single" || selectedPackage.package_id === "double") {
      const end = selectedDates.end || selectedDates.start;
      const days = Math.max(1, Math.ceil((end - selectedDates.start) / (1000 * 60 * 60 * 24)) + 1);
      return (selectedPackage.price * days).toFixed(2);
    }
    return selectedPackage.price.toFixed(2);
  };

  return (
    <div className="min-h-screen pt-36 bg-stone-50" data-testid="book-page">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4">Cat Sitting Services</h1>
          <p className="text-stone-600 max-w-2xl mx-auto">
            Going on holiday? Working long hours? Let our cat-loving team care for your furry friend.
          </p>
        </div>

        {/* Important Notice Banner */}
        <Card className="mb-8 bg-amber-50 border-amber-200">
          <CardContent className="p-6">
            <h3 className="font-bold text-amber-800 mb-3 flex items-center gap-2">
              <span className="text-xl">📋</span> Important Booking Information
            </h3>
            <div className="space-y-3 text-sm text-amber-900">
              <p>
                <strong>Overnight Stay Note:</strong> The first day of an overnight booking, if the AM slot is required, you will need to book a single visit on the day of the overnight stay. If the PM slot is required at the end of your booking, you will also need to book a single visit for the PM. Please <a href="mailto:Team@CatPals.co.uk" className="underline font-semibold">contact us</a> to arrange this manually.
              </p>
              <p>
                <strong>Mileage Charge:</strong> All bookings are subject to a mileage charge if you live more than 8 miles from NR2 2SA at <strong>50 pence per mile</strong>.
              </p>
              <p>
                If this applies to you, please book by calling <a href="tel:01603555574" className="underline font-semibold">(01603) 555574</a>, <a href="tel:07754754758" className="underline font-semibold">(07754) 754758</a> or email <a href="mailto:Team@CatPals.co.uk" className="underline font-semibold">Team@CatPals.co.uk</a>
              </p>
              <p>
                <strong>Microchipping:</strong> We also offer microchipping services! <Link to="/microchipping" className="underline font-semibold text-amber-700 hover:text-amber-800">Click here for details</Link>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Packages */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {packages.map(pkg => (
            <Card 
              key={pkg.package_id} 
              className={`cursor-pointer transition-all hover:shadow-lg ${selectedPackage?.package_id === pkg.package_id ? "ring-2 ring-amber-600 bg-amber-50" : ""}`}
              onClick={() => setSelectedPackage(pkg)}
              data-testid={`package-${pkg.package_id}`}
            >
              <CardHeader>
                <CardTitle className="text-lg">{pkg.name}</CardTitle>
                <CardDescription>{pkg.duration}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-amber-600">£{pkg.price.toFixed(2)}</p>
                <p className="text-sm text-stone-500 mt-2">{pkg.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {selectedPackage && (
          <Card className="max-w-4xl mx-auto animate-fadeIn" data-testid="booking-form">
            <CardHeader>
              <CardTitle>Book {selectedPackage.name}</CardTitle>
              <CardDescription>Select your dates and provide your cat's details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <Label className="mb-2 block">Select Dates</Label>
                  <CalendarComponent
                    mode={selectedPackage.package_id === "single" || selectedPackage.package_id === "double" ? "range" : "single"}
                    selected={selectedPackage.package_id === "single" || selectedPackage.package_id === "double" 
                      ? { from: selectedDates.start, to: selectedDates.end }
                      : selectedDates.start}
                    onSelect={(val) => {
                      if (selectedPackage.package_id === "single" || selectedPackage.package_id === "double") {
                        setSelectedDates({ start: val?.from, end: val?.to });
                      } else {
                        setSelectedDates({ start: val, end: val });
                      }
                    }}
                    disabled={(date) => {
                      const dateStr = date.toISOString().split("T")[0];
                      return date < new Date() || bookedDates.includes(dateStr) || blockedDates.includes(dateStr);
                    }}
                    className="rounded-lg border"
                    data-testid="booking-calendar"
                  />
                </div>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="catName">Your Cat's Name *</Label>
                    <Input 
                      id="catName" 
                      value={catName} 
                      onChange={e => setCatName(e.target.value)} 
                      placeholder="e.g., Whiskers"
                      className="mt-1"
                      data-testid="cat-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="notes">Special Instructions</Label>
                    <Textarea 
                      id="notes" 
                      value={notes} 
                      onChange={e => setNotes(e.target.value)} 
                      placeholder="Dietary requirements, medication, favorite toys..."
                      className="mt-1"
                      rows={4}
                      data-testid="notes-input"
                    />
                  </div>
                  {selectedDates.start && (
                    <Card className="bg-stone-100 border-0">
                      <CardContent className="p-4">
                        <div className="flex justify-between items-center">
                          <span className="text-stone-600">Total Price</span>
                          <span className="text-2xl font-bold text-amber-600">£{calculatePrice()}</span>
                        </div>
                        {selectedPackage.package_id === "nailclip" && (
                          <p className="text-xs text-stone-500 mt-2">* Subject to the cat being willing</p>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex flex-col gap-4">
              <Button 
                onClick={handleBook} 
                disabled={loading || !selectedDates.start || !catName}
                className="w-full bg-amber-600 hover:bg-amber-700 rounded-full py-6 text-lg"
                data-testid="book-submit-btn"
              >
                {loading ? "Processing..." : `Book & Pay £${calculatePrice()}`}
              </Button>
              <div className="flex items-center justify-center gap-2 text-sm text-stone-500">
                <span>Secure checkout with</span>
                <img 
                  src="https://www.paypalobjects.com/webstatic/mktg/Logo/pp-logo-200px.png" 
                  alt="PayPal" 
                  className="h-5"
                />
              </div>
            </CardFooter>
          </Card>
        )}
      </div>
    </div>
  );
};

const BookingSuccessPage = () => {
  const [status, setStatus] = useState("checking");
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const bookingId = params.get("booking_id");
  const paymentId = params.get("paymentId");
  const payerId = params.get("PayerID");
  const executedRef = useRef(false);

  useEffect(() => {
    if (executedRef.current) return;
    executedRef.current = true;

    const executePayment = async () => {
      // If we have PayPal params, execute the payment
      if (paymentId && payerId && bookingId) {
        try {
          const res = await axios.get(`${API}/checkout/execute?paymentId=${paymentId}&PayerID=${payerId}&booking_id=${bookingId}`);
          if (res.data.payment_status === "paid" || res.data.status === "success") {
            setStatus("success");
          } else {
            setStatus("error");
          }
        } catch {
          setStatus("error");
        }
      } else if (bookingId) {
        // Just check status
        try {
          const res = await axios.get(`${API}/checkout/status/${bookingId}`);
          if (res.data.payment_status === "paid") {
            setStatus("success");
          } else {
            setStatus("pending");
          }
        } catch {
          setStatus("error");
        }
      } else {
        setStatus("error");
      }
    };
    executePayment();
  }, [paymentId, payerId, bookingId]);

  return (
    <div className="min-h-screen pt-36 flex items-center justify-center bg-stone-50" data-testid="booking-success-page">
      <Card className="max-w-md w-full mx-4 text-center">
        <CardContent className="p-8">
          {status === "checking" && (
            <>
              <div className="animate-spin w-12 h-12 border-4 border-amber-600 border-t-transparent rounded-full mx-auto mb-4" />
              <h2 className="text-xl font-semibold">Confirming your payment...</h2>
              <p className="text-stone-500 mt-2">Please wait while we verify your booking.</p>
            </>
          )}
          {status === "success" && (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-stone-900 mb-2">Booking Confirmed!</h2>
              <p className="text-stone-600 mb-6">Thank you for booking with CatPals+. We'll send you a confirmation email shortly.</p>
              <Button onClick={() => navigate("/dashboard")} className="bg-amber-600 hover:bg-amber-700 rounded-full" data-testid="go-dashboard-btn">
                View My Bookings
              </Button>
            </>
          )}
          {(status === "error" || status === "pending") && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-stone-900 mb-2">Something went wrong</h2>
              <p className="text-stone-600 mb-6">We couldn't confirm your payment. Please contact us or try again.</p>
              <Button onClick={() => navigate("/book")} variant="outline" className="rounded-full">
                Try Again
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export { BookPage, BookingSuccessPage };
